import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as Minus } from 'assets/icons/minus.svg';
import { ReactComponent as Plus } from 'assets/icons/plus.svg';
import { ReactComponent as Delete } from 'assets/icons/times.svg';
import { isNonFundraiserProduct } from 'redux/helpers';
import {
  cartSelector,
  deleteProduct,
  editCartProductTemp,
  updateCartProduct
} from 'redux/slices/cart';
import debounce from 'lodash/debounce';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  generateItemAmountsSchema,
  generateItemAmountsFormDefaultValues
} from './Cart.schema';
import { showErrorMessage } from 'redux/slices/notification';
import { CURRENCY_SYMBOL } from 'utils/transform';
import {
  previewStripeTerminalOrder,
} from 'redux/slices/checkout';

const Cart = () => {
  const dispatch = useDispatch();
  const editingProduct = useSelector((state) => state.cart.editingProduct);
  const cart = useSelector(cartSelector.selectAll);
  const onClickDelete = (cartProduct) => {
    dispatch(deleteProduct({ cartProduct }));
  };

  const {
    shippingAddressDisabled,
    renderStripeForm,
    checkoutStep,
  } = useSelector(state => state.checkout);

  const { setValue, control } = useForm({
    resolver: yupResolver(generateItemAmountsSchema(cart)),
    defaultValues: generateItemAmountsFormDefaultValues(cart)
  });

  const form = useWatch({ control });

  const onClickEdit = (cartProduct) => {
    dispatch(editCartProductTemp(cartProduct));
  };

  const updateAmount = useCallback((amount, productId) => {
    dispatch(
      updateCartProduct({
        cartProductId: productId,
        payload: {
          quantity: amount,
        }
      })
    )
  }, [dispatch]);

  const debounceUpdateAmount = useMemo(() => {
    return debounce(updateAmount, 500);
  }, [updateAmount]);

  const handleProductAmountChange = (amount, item) => {
    const productId = item.id;

    const maxPerOrder = item?.productable?.order_limit || 200;

    const normalizedAmount = Number(String(amount).replace(/^0+/, ''));

    if (amount.length > 3) {
      return;
    };

    if (normalizedAmount > maxPerOrder) {
      setValue(productId, maxPerOrder);
      dispatch(showErrorMessage(`Cannot buy more than ${maxPerOrder} units in one order`));
      debounceUpdateAmount(maxPerOrder, productId);
      return;
    }

    if (amount === '') {
      setValue(productId, amount);
      return;
    }

    if (amount < 1) {
      setValue(productId, 1);
    } else {
      setValue(productId, normalizedAmount);
    }

    debounceUpdateAmount(amount < 1 ? 1 : amount, productId);
  };

  const handleProductAmountInputBlur = (amount, item) => {
    const productId = item.id;

    if (amount === '') {
      dispatch(showErrorMessage("Minimum order amount is 1 unit"));
      setValue(item.id, 1);
      debounceUpdateAmount(1, productId)
    };
  }

  const handleProductAmountIncrease = (item, currentQuantity) => {
    const newQuantity = Number(currentQuantity) + 1;

    handleProductAmountChange(newQuantity, item);
  };

  const handleProductAmountDecrease = (item, currentQuantity) => {
    const newQuantity = Number(currentQuantity) - 1;

    handleProductAmountChange(newQuantity, item);
  };

  const storeIdFromUrl = useSelector((state) => state.javelinStore.currentStoreId);
  const { canPreviewTerminalOrder, loading: checkoutLoading } = useSelector((state) => state.checkout);

  useEffect(() => {

    if (storeIdFromUrl && canPreviewTerminalOrder && !checkoutLoading && checkoutStep !== 1) {
      dispatch(previewStripeTerminalOrder({ store: storeIdFromUrl }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, storeIdFromUrl, canPreviewTerminalOrder, checkoutLoading])

  return (
    <div data-testid="cart-list" className="mb-4 lg:mb-8">
      {cart.map((cartItem) => {
        const maxPerOrder = cartItem?.productable?.order_limit || 200;

        return (
          !!cartItem.id ? (
            <div key={cartItem.id} className="grid grid-cols-2 my-3">
              <div className="flex overflow-hidden">
                <div
                  data-testid="image"
                  className="w-7 h-7 lg:w-12 lg:h-12 bg-cover bg-no-repeat bg-center border border-javelin-gray-border flex-shrink-0"
                  style={{
                    backgroundImage: `url(${cartItem.product?.image})`,
                  }}
                />
                <div
                  data-testid="product detail"
                  className="pl-1 lg:pl-2 my-auto box-border"
                >
                  <p className="text-xs lg:text-sm lg:mb-1 overflow-hidden w-8/12 truncate">
                    {cartItem.product?.name}
                  </p>
                  <p className="text-xs text-javelin-gray-dark text-opacity-30 w-24 mr-2 lg:w-40 overflow-ellipsis whitespace-nowrap overflow-hidden">
                    {cartItem.product?.description}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                {isNonFundraiserProduct(cartItem) ? (
                  <div
                    data-testid="product-count"
                    className="grid col-span-2 grid-cols-3 self-center h-6 lg:h-9 bg-white justify-between cursor-not-allowed"
                  >
                    <button
                      onClick={() => handleProductAmountDecrease(cartItem, cartItem.quantity)}
                      disabled={renderStripeForm && (shippingAddressDisabled || (Number(form[cartItem.id]) <= 1))}
                      className="border-0 lg:border lg:border-r-0 lg:border-javelin-gray-border lg:rounded-l focus:outline-none hover:bg-gray-50 disabled:bg-gray-50 cursor-pointer disabled:cursor-not-allowed w-6 h-6 lg:w-9 lg:h-9 flex items-center justify-center"
                    >
                      <Minus className="w-3 h-4 text-opacity-30 text-javelin-gray-dark fill-current" />
                    </button>
                    <input
                      value={form[cartItem.id]}
                      onChange={({ target }) => handleProductAmountChange(target.value, cartItem)}
                      type='number'
                      disabled={renderStripeForm && shippingAddressDisabled}
                      onBlur={({ target }) => handleProductAmountInputBlur(target.value, cartItem)}
                      className="p-0 border-0 text-center lg:border lg:border-javelin-gray-border w-6 h-6 lg:w-9 lg:h-9 text-xs text-javelin-gray-dark flex items-center justify-center bg-gray-50"
                    />
                    <button
                      onClick={() => handleProductAmountIncrease(cartItem, cartItem.quantity)}
                      disabled={renderStripeForm && (shippingAddressDisabled || (Number(form[cartItem.id]) > maxPerOrder))}
                      className="border-0 lg:border lg:border-l-0 lg:border-javelin-gray-border lg:rounded-r focus:outline-none hover:bg-gray-50 disabled:bg-gray-50 cursor-pointer disabled:cursor-not-allowed w-6 h-6 lg:w-9 lg:h-9 flex items-center justify-center"
                    >
                      <Plus className="w-3 h-4 text-opacity-30 text-javelin-gray-dark fill-current" />
                    </button>
                  </div>
                ) : (
                  <button
                    className="btn text-javelin-blue-dark text-sm text-center cursor-pointer w-20 disabled:cursor-not-allowed"
                    disabled={editingProduct !== null}
                    onClick={() => onClickEdit(cartItem)}
                  >
                    Edit
                  </button>
                )}
                <Delete
                  className="w-3 h-4 text-opacity-30 text-javelin-gray-dark fill-current cursor-pointer mr-2 inline-block"
                  onClick={() => onClickDelete(cartItem)}
                />
                <span className="text-sm text-javelin-gray-dark text-right lg:w-24">
                  {CURRENCY_SYMBOL[cartItem.productable.currency]}{cartItem.priceToDisplay}
                </span>
              </div>
            </div>
          ) : null
        )
      })}
    </div>
  );
};

export default Cart;
